import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import _ from 'lodash';
import { authService } from '../../../shared/services/auth/authService';
import { mapUser } from '../../../utils/userMapper';

interface AuthContextProps {
  user: any;
  isAuthenticated: boolean;
  selectedTenant: any;
  changeSelectedTenant: (tenant: any) => void;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('auth_user'));
  const [selectedTenant, setSelectedTenant] = useState<any|null|undefined>(() => {
    const tenant = localStorage.getItem('selected_tenant');
    if (tenant) {
      return JSON.parse(tenant);
    }
    return null;
  });
  const [user, setUser] = useState<any>(() => {
    const stored = localStorage.getItem('auth_user');
    return stored ? JSON.parse(stored) : null;
  });

  useEffect(() => {
    const checkAuth = () => {
      const authUser = localStorage.getItem('auth_user');
      setIsAuthenticated(!!authUser);
      setUser(authUser ? JSON.parse(authUser) : null);
    };

    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  const login = async (username: string, password: string) => {
    if (!username || !password) {
      return { success: false, error: 'Usuário e senha são obrigatórios.' };
    }

    try {
      const response = await authService.login(username, password);
      const { access_token, user: userData } = response.data;
      const newUser = mapUser(userData, {});
      loginSuccess(newUser, access_token);
      return { success: true };
    } catch (error: any) {
      let errorMsg = 'Não foi possível fazer login. Por favor, verifique suas credenciais e tente novamente. Caso o problema persista, contate o suporte.';
      if (typeof error?.response?.data?.message === 'string') {
        errorMsg = error.response.data.message;
      } else if (typeof error?.response?.data === 'string') {
        errorMsg = error.response.data;
      }
      return { success: false, error: errorMsg };
    }
  };

  const loginSuccess = (newUser: any, access_token: any) => {
    setSelectedTenant(newUser.tenants?.[0]);
    setIsAuthenticated(true);
    setUser(newUser);
    localStorage.setItem('auth_user', JSON.stringify(newUser));
    localStorage.setItem('access_token', access_token);
    if (newUser.tenants?.[0]) {
      localStorage.setItem('selected_tenant', JSON.stringify(newUser.tenants[0]));
    }
  }

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('auth_user');
    localStorage.removeItem('access_token');
    localStorage.removeItem('selected_tenant');
  };

  const changeSelectedTenant = (tenant: any) => {
    setSelectedTenant(tenant);
    localStorage.setItem('selected_tenant', JSON.stringify(tenant));
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, selectedTenant, changeSelectedTenant, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}